import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {StyledJournalEntryTabContainer} from '../../../NetSuiteJournalEntriesPage/JournalEntriesTabs/StyledJournalEntriesTabs';
import TableFilters from '../../TableFilters';
import EmptyOrTable from '../../../../EmptyOrTable';
import {accountingHelpers, formHelpers} from '../../../../../utils/helpers';
import {useAccountingOptions} from '../../../../../hooks';

const defaultFilterProps = accountingHelpers.getHistoryTabFilterProps();

const VendorsHistoryTab = ({
  data = [],
  empty,
  loading = false,
  onFilter,
  onRowUpdate,
  onRowSelect,
  tableFiltersProps,
  tableProps,
  selectedRowsIds = [],
  ...rest
}) => {
  const [t] = useTranslation('main');
  const [query, setQuery] = useState({ search: '' });

  const { numberingFlowsOptions, taxCodeOptions} = useAccountingOptions();

  const handleOnFilter = (filterParams) => {
    setQuery({...query, ...filterParams});
    onFilter?.(filterParams);
  }

  const getInput = ({ defaultValue, propName, record }) => {
    if (!accountingHelpers.isVendorSyncStatusError(record?.sync_status)) return defaultValue;

    return accountingHelpers.getTableInput({
      defaultValue,
      propName,
      record,
      t,
      onChange: (value) => onRowUpdate?.({id: record.id, data: {[propName]: value}}),
    });
  };

  const getSelect = ({ defaultValue, options, propName, record, ...rest }) => {
    if (!accountingHelpers.isVendorSyncStatusError(record?.sync_status)) {
      return formHelpers.getOptionValueLabel(options, defaultValue);
    }

    return accountingHelpers.getTableSelect({
      defaultValue,
      options,
      propName,
      record,
      t,
      onSelect: (value) => onRowUpdate?.({ id: record.id, data: { [propName]: value } }),
      ...rest
    });
  };

  const columns = [
    { dataIndex: 'remote_id', title: t('vendorReference') },
    { dataIndex: 'name', title: t('name') },
    { dataIndex: 'sync_status', title: t('status'), render: accountingHelpers.getSyncStatus },
    {
      dataIndex: 'tax_number',
      title: t('vat'),
      render: (defaultValue, record) => getInput({ defaultValue, propName: 'tax_number', record })
    },
    {
      dataIndex: 'country',
      title: t('country'),
      render: (defaultValue, record) => getInput({ defaultValue, propName: 'country', record })
    },
    {
      dataIndex: 'address',
      title: t('address'),
      render: (defaultValue, record) => getInput({ defaultValue, propName: 'address', record })
    },
    {
      dataIndex: 'city',
      title: t('city'),
      render: (defaultValue, record) => getInput({ defaultValue, propName: 'city', record })
    },
    {
      dataIndex: 'state_province',
      title: t('state'),
      render: (defaultValue, record) => getInput({ defaultValue, propName: 'state_province', record })
    },
    {
      dataIndex: 'email_address',
      title: t('email'),
      render: (defaultValue, record) => getInput({ defaultValue, propName: 'email_address', record })
    },
    {
      dataIndex: 'vendor_tax_code',
      render: (defaultValue, record) => getSelect({ defaultValue, record, options: taxCodeOptions, propName: 'vendor_tax_code' }),
      title: t('taxCode'),
    },
    {
      dataIndex: 'default_numbering_flow',
      render: (defaultValue, record) => getSelect({ defaultValue, record, options: numberingFlowsOptions, propName: 'default_numbering_flow', placeholderPropName: 'numberingFlow' }),
      title: t('numberingFlow'),
    }
  ];

  return (
    <StyledJournalEntryTabContainer
      {...rest}
      size='large'
      direction='vertical'
    >
      <TableFilters
        {...tableFiltersProps}
        {...defaultFilterProps}
        onFilter={handleOnFilter}
      />
      <EmptyOrTable
        data={data}
        empty={empty}
        loading={loading}
        query={query}
        columns={columns}
        tableProps={tableProps}
      />
    </StyledJournalEntryTabContainer>
  );
}

VendorsHistoryTab.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.array,
  empty: PropTypes.any,
  loading: PropTypes.bool,
  onFilter: PropTypes.func,
  onRowUpdate: PropTypes.func,
  selectedRowsIds: PropTypes.array,
  tableFiltersProps: PropTypes.any,
  tableProps: PropTypes.object,
}

export default VendorsHistoryTab;
